import React, { useEffect, useState } from "react";
import api, { apiError } from "../api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AjutorButton } from "../AjutorButton";
import { usePermissions } from "../AuthContext";

const wrap = { fontFamily: "Arial", fontSize: 14, color: "#000", background: "#fff" };
const inp = {
  border: "1px solid #000", padding: "6px 8px", fontFamily: "Arial",
  fontSize: 14, color: "#000", background: "#fff", outline: "none", width: "100%",
};
const lbl = { display: "block", marginBottom: 4, fontWeight: "bold" };

const FIELDS = [
  { key: "name", label: "Denumire firma" },
  { key: "cui", label: "CUI" },
  { key: "reg_com", label: "Nr. Reg. Com." },
  { key: "euid", label: "EUID" },
  { key: "country", label: "Tara" },
  { key: "county", label: "Judet" },
  { key: "locality", label: "Localitate" },
  { key: "street", label: "Strada si numar" },
  { key: "postal_code", label: "Cod postal" },
  { key: "bank", label: "Banca" },
  { key: "iban_ron", label: "IBAN RON" },
  { key: "iban_eur", label: "IBAN EUR" },
  { key: "swift", label: "SWIFT / BIC" },
];

const EMPTY = FIELDS.reduce((a, f) => ({ ...a, [f.key]: "" }), { vat_rate: "", bnr_rate_manual: "", bnr_rate_date: "" });

export default function CompanyProfile() {
  const { can } = usePermissions();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPlatform, setIsPlatform] = useState(false);

  useEffect(() => {
    api.get("/company-profile")
      .then(({ data }) => {
        const next = { ...EMPTY };
        FIELDS.forEach((f) => { next[f.key] = data[f.key] ?? ""; });
        next.vat_rate = data.vat_rate ?? "";
        next.bnr_rate_manual = data.bnr_rate_manual ?? "";
        next.bnr_rate_date = data.bnr_rate_date ?? "";
        setForm(next);
        setIsPlatform(data.key === "platform");
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.vat_rate = form.vat_rate === "" ? null : Number(form.vat_rate);
      payload.bnr_rate_manual = form.bnr_rate_manual === "" ? null : Number(form.bnr_rate_manual);
      payload.bnr_rate_date = form.bnr_rate_date === "" ? null : form.bnr_rate_date;
      const { data } = await api.put("/company-profile", payload);
      const next = { ...EMPTY };
      FIELDS.forEach((f) => { next[f.key] = data[f.key] ?? ""; });
      next.vat_rate = data.vat_rate ?? "";
      next.bnr_rate_manual = data.bnr_rate_manual ?? "";
      next.bnr_rate_date = data.bnr_rate_date ?? "";
      setForm(next);
      toast.success("Date firma salvate");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={wrap}><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div data-testid="company-profile-page" style={wrap}>
      <AjutorButton file="ajutor_date_firma.pdf" />
      <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 16 }}>Date firma</div>
      <div style={{ maxWidth: 760 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {FIELDS.map((f) => (
            <div key={f.key} style={f.key === "street" ? { gridColumn: "1 / -1" } : {}}>
              <label style={lbl}>{f.label}</label>
              <input
                data-testid={`company-${f.key}-input`}
                value={form[f.key]}
                onChange={set(f.key)}
                style={inp}
              />
            </div>
          ))}
          <div>
            <label style={lbl}>Cota TVA (%)</label>
            <input
              data-testid="company-vat_rate-input"
              type="number"
              value={form.vat_rate}
              onChange={set("vat_rate")}
              style={inp}
            />
          </div>
        </div>
        {isPlatform && (
          <div style={{ marginTop: 20, borderTop: "1px solid #000", paddingTop: 12 }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>
              Curs BNR EUR/RON (manual - doar daca preluarea automata de pe bnr.ro esueaza)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={lbl}>Curs BNR EUR/RON</label>
                <input
                  data-testid="company-bnr_rate_manual-input"
                  type="number"
                  step="0.0001"
                  value={form.bnr_rate_manual}
                  onChange={set("bnr_rate_manual")}
                  style={inp}
                />
              </div>
              <div>
                <label style={lbl}>Data cursului (AAAA-LL-ZZ)</label>
                <input
                  data-testid="company-bnr_rate_date-input"
                  placeholder="2026-06-15"
                  value={form.bnr_rate_date}
                  onChange={set("bnr_rate_date")}
                  style={inp}
                />
              </div>
            </div>
          </div>
        )}
        <button
          data-testid="company-save-button"
          onClick={save}
          disabled={saving || !can("date_firma", "edit")}
          style={{ marginTop: 16, border: "1px solid #000", background: "#000", color: "#fff", padding: "8px 20px", fontFamily: "Arial", fontSize: 14, cursor: "pointer", opacity: (saving || !can("date_firma", "edit")) ? 0.6 : 1, display: can("date_firma", "edit") ? "inline-block" : "none" }}
        >
          {saving ? "Se salveaza..." : "Salveaza"}
        </button>
      </div>
    </div>
  );
}
